import { Inject, Injectable } from '@angular/core';
import { bindCallback } from 'rxjs';
import { Loader, google, LoaderOptions } from 'google-maps';

import { CoreServiceConfig, CORE_SERVICES_CONFIG } from '../../core-services.config';
import { TimezoneResponse } from '../models';

const options: LoaderOptions = {
  libraries: ['places'],
};

/** @abstract timezone in second without Daylight Saving Time (is removed) */
const buildGetTz = (config: CoreServiceConfig) => {
  const cache = new Map<string, Promise<TimezoneResponse | null>>();

  return async (location: google.maps.LatLng): Promise<TimezoneResponse | null> => {
    const lat = location.lat().toPrecision(7);
    const lng = location.lng().toPrecision(7);
    const cacheKey = `${lat},${lng}`;

    if (cache.has(cacheKey)) {
      return cache.get(cacheKey) ?? null;
    }

    const params = {
      key: config.timezone.apiKey,
      format: 'json',
      by: 'position',
      lat,
      lng,
    };

    const url = new URL(config.timezone.url);
    for (const [key, param] of Object.entries(params)) {
      url.searchParams.append(key, param);
    }

    const responsePromise = fetch(url.toString()).then(async response => {
      if (response.status === 200) {
        const data = await response.json();
        if (data.status === 'OK') {
          const nextYearFromNow = () => {
            const now = new Date();
            return new Date(now.getFullYear() + 1, now.getMonth(), now.getDate(), now.getHours(), now.getMinutes(), now.getSeconds());
          };
          return {
            ...data,
            zoneEnd: nextYearFromNow().getTime(),
          };
        }
      }
      return null;
    }).catch(() => null);

    cache.set(cacheKey, responsePromise);
    return responsePromise;
  };
};

@Injectable({
  providedIn: 'root',
})
export class MapService {
  private GoogleMapsLoader;
  private config?: CoreServiceConfig;
  private google?: google;
  private geocoder?: google.maps.Geocoder;

  /** @abstract return gmtOffset in secs */
  readonly getTz;

  constructor(@Inject(CORE_SERVICES_CONFIG) config: CoreServiceConfig) {
    this.config = config;
    this.GoogleMapsLoader = new Loader(this.config.firebaseConfig.apiKey, options);
    this.getTz = buildGetTz(this.config);
  }

  async getMarker(map: google.maps.Map<HTMLElement>, position: google.maps.LatLng) {
    this.google = this.google ?? await this.GoogleMapsLoader.load();
    return new this.google.maps.Marker({
      map,
      position,
    });
  }

  async getLatLng(value: { lat: number; lng: number }) {
    this.google = this.google ?? await this.GoogleMapsLoader.load();
    return  new google.maps.LatLng(value.lat, value.lng);
  }

  async getMap(element: HTMLElement, opts: google.maps.MapOptions) {
    this.google = this.google ?? await this.GoogleMapsLoader.load();
    const latlng = new this.google.maps.LatLng(41.884326767819786, 12.480541269505478);
    return new this.google.maps.Map(element, {
      center: latlng,
      draggable: true,
      mapTypeControl: false,
      streetViewControl: false,
      zoom: 8,
      ...opts,
    });
  }

  async codeAddress(address: string) {
    this.google = this.google ?? await this.GoogleMapsLoader.load();
    this.geocoder = this.geocoder ?? new this.google.maps.Geocoder();
    const config = { address };
    const getCode = bindCallback(this.geocoder.geocode);
    const [results, status] = await getCode(config).toPromise();
    const success = status === google.maps.GeocoderStatus.OK;
    return success ? results : [];
  }
}
